"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import Icon from "@/components/ui/AppIcon";
import { businesses, CATEGORIES } from "@/lib/data";

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<typeof businesses>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const handleSearch = useCallback((q: string) => {
    setQuery(q);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const lower = q.toLowerCase();
    const filtered = businesses.filter(
      (b) =>
        b.name.toLowerCase().includes(lower) ||
        b.category.toLowerCase().includes(lower) ||
        b.district.toLowerCase().includes(lower) ||
        b.description.toLowerCase().includes(lower) ||
        b.tags.some((t) => t.toLowerCase().includes(lower))
    );
    setResults(filtered.slice(0, 6));
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = "hidden";
    } else {
      setQuery("");
      setResults([]);
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    router.push(`/search?q=${encodeURIComponent(query.trim())}`);
    onClose();
  };

  const handleResultClick = (id: string) => {
    router.push(`/business-detail?id=${id}`);
    onClose();
  };

  const handleCategoryClick = (slug: string) => {
    router.push(`/directory?category=${slug}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(20px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Close button */}
      <div className="flex justify-end p-4 sm:p-6">
        <button
          onClick={onClose}
          className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all"
          aria-label="Kapat"
        >
          <Icon name="XMarkIcon" size={28} />
        </button>
      </div>

      {/* Search area */}
      <div className="flex-1 flex flex-col items-center px-4 sm:px-8 pt-4 sm:pt-8 overflow-y-auto">
        <div className="w-full max-w-2xl">
          {/* Title */}
          <h2 className="text-white/60 text-sm font-semibold uppercase tracking-widest mb-6 text-center">
            Konya'da Ara
          </h2>

          {/* Search input */}
          <form onSubmit={handleSubmit} className="relative mb-8">
            <div className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-2xl px-5 py-4 focus-within:border-white/50 focus-within:bg-white/15 transition-all">
              <Icon name="MagnifyingGlassIcon" size={22} className="text-white/60 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="İşletme, kategori veya ilçe ara..."
                className="flex-1 bg-transparent text-white text-lg placeholder-white/40 outline-none"
                autoComplete="off"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => handleSearch("")}
                  className="text-white/40 hover:text-white/80 transition-colors"
                >
                  <Icon name="XMarkIcon" size={18} />
                </button>
              )}
            </div>
            {query.trim() && (
              <button
                type="submit"
                className="mt-3 w-full py-3 rounded-xl font-bold text-white text-sm transition-all"
                style={{ background: "var(--primary)" }}
              >
                Tüm Sonuçları Gör →
              </button>
            )}
          </form>

          {/* Live results */}
          {results.length > 0 && (
            <div className="mb-8">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-3">
                Hızlı Sonuçlar
              </p>
              <div className="flex flex-col gap-2">
                {results.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => handleResultClick(b.id)}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/8 hover:bg-white/15 border border-white/10 hover:border-white/25 transition-all text-left group"
                  >
                    <div
                      className="w-12 h-12 rounded-xl bg-cover bg-center shrink-0"
                      style={{ backgroundImage: `url(${b.coverImage})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate group-hover:text-primary transition-colors">
                        {b.name}
                      </p>
                      <p className="text-white/50 text-xs mt-0.5">
                        {b.category} · {b.district}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-yellow-400 shrink-0">
                      <Icon name="StarIcon" size={13} />
                      <span className="text-white/70 text-xs">{b.rating}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No results */}
          {query.trim() && results.length === 0 && (
            <div className="text-center py-8 mb-8">
              <p className="text-white/40 text-base">
                "<span className="text-white/70">{query}</span>" için sonuç bulunamadı
              </p>
              <p className="text-white/30 text-sm mt-2">Farklı bir arama terimi deneyin</p>
            </div>
          )}

          {/* Quick categories (shown when no query) */}
          {!query && (
            <div>
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-4">
                Popüler Kategoriler
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {CATEGORIES.slice(0, 8).map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryClick(cat.slug)}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/8 hover:bg-white/15 border border-white/10 hover:border-white/25 transition-all"
                  >
                    <span className="text-2xl">{cat.emoji}</span>
                    <span className="text-white/70 text-xs font-medium">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
